import useMediaQuery from "@/hooks/useMediaQuery";
import { breakpoints } from "@/styles/constants";
import { EventDetailsCard } from "@app/core/src/shared/modules/events/components/event-details/EventDetailsCard/EventDetailsCard.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import styled from "@emotion/styled";
import { PublicBaseEventSerializer } from "@shared/modules/events/types/public";
import { useTranslation } from "@shared/utils/internationalization";
import React from "react";
import SwiperCore, { Autoplay, Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css"; // Import Swiper styles

const { lg } = breakpoints;

const EventSliderContainer = styled(Flex)`
  .swiper-container {
    max-width: 100% !important;
    width: 100% !important;
    height: auto;
  }

  .swiper-slide {
    max-width: 50%;
    width: 50%;
    height: auto;
    padding: 3px; // prevents the card border from overflowing the swiper container and being hidden
  }

  .swiper-button-prev {
    color: var(--chakra-colors-fg);
  }

  .swiper-button-next {
    color: var(--chakra-colors-fg);
  }

  @media only screen and (min-width: 0px) and (max-width: ${lg}px) {
    .swiper-slide {
      max-width: 100%;
      width: 100%;
      height: auto;
    }
  }
`;

export interface UpcomingEventsSliderProps {
  upcomingEvents: PublicBaseEventSerializer[];
}

const UpcomingEventsSlider: React.FC<UpcomingEventsSliderProps> = ({
  upcomingEvents,
}) => {
  // Needed for auto-swiping event cards on mobile
  SwiperCore.use([Navigation, Autoplay]);
  const [isTablet] = useMediaQuery(`(min-width: 0px) and (max-width: ${lg}px)`);

  const { t } = useTranslation("common");

  const getEventCard = (event: PublicBaseEventSerializer) => {
    return (
      <EventDetailsCard
        isLoading={false}
        event={event}
        tracking_label_suffix="upcoming-events-slider"
      />
    );
  };

  return (
    <>
      {/* Single upcoming event displayed without swiper */}
      {upcomingEvents.length === 1 && (
        <Flex justifyContent="center" width="100%" p="0px 50px">
          <Flex width="100%" maxWidth="332px">
            {getEventCard(upcomingEvents[0])}
          </Flex>
        </Flex>
      )}

      {/* Multiple upcoming events displayed with swiper */}
      {upcomingEvents.length > 1 && (
        <EventSliderContainer height="auto" alignItems="center">
          <IconButton
            iconName="ChevronLeft"
            iconOutline={false}
            iconColor="font.dark"
            iconSize="20px"
            className="prev"
            aria-label={t("Previous event")}
            colorPalette="light"
          />
          <Swiper
            navigation={{
              prevEl: ".prev",
              nextEl: ".next",
            }}
            loop
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            spaceBetween={20}
            slidesPerView={isTablet ? 1 : 2}
          >
            {upcomingEvents.map((event: PublicBaseEventSerializer) => (
              <SwiperSlide key={event.slug}>
                <Flex width="100%" justifyContent="center" height="100%">
                  <Flex width="100%" maxWidth="332px">
                    {getEventCard(event)}
                  </Flex>
                </Flex>
              </SwiperSlide>
            ))}
          </Swiper>
          <IconButton
            iconName="ChevronRight"
            iconOutline={false}
            iconColor="font.dark"
            iconSize="20px"
            className="next"
            aria-label={t("Next event")}
            colorPalette="light"
          />
        </EventSliderContainer>
      )}
    </>
  );
};

export default UpcomingEventsSlider;
